import React from 'react';
import {
  SiSpringboot,
  SiDotnet,
  SiGo,
  SiNodedotjs,
  SiReact,
  SiTypescript,
  SiAmazon,
  SiGooglecloud,
  SiDocker,
  SiKubernetes,
  SiPostgresql,
  SiMongodb,
  SiRedis,
  SiRabbitmq,
  SiApachekafka,
  SiGithubactions,
  SiJenkins,
  SiTerraform
} from 'react-icons/si';
import { FaMobile, FaDatabase, FaCloud, FaCode, FaServer, FaCogs, FaMicrosoft } from 'react-icons/fa';

const TechStack: React.FC = () => {
  const categories = [
    {
      title: 'Backend Development',
      icon: <FaServer className="text-3xl" />,
      color: 'from-green-500 to-emerald-600',
      technologies: [
        { name: 'Java / Spring Boot', icon: <SiSpringboot className="text-4xl text-green-600" />, description: 'Enterprise-grade backend systems' },
        { name: '.NET Core / ASP.NET', icon: <SiDotnet className="text-4xl text-purple-600" />, description: 'Scalable Microsoft stack solutions' },
        { name: 'Golang', icon: <SiGo className="text-4xl text-cyan-600" />, description: 'High-performance microservices' },
        { name: 'Node.js', icon: <SiNodedotjs className="text-4xl text-green-500" />, description: 'Real-time & async applications' }
      ]
    },
    {
      title: 'Frontend Development',
      icon: <FaCode className="text-3xl" />,
      color: 'from-blue-500 to-indigo-600',
      technologies: [
        { name: 'React', icon: <SiReact className="text-4xl text-blue-500" />, description: 'Modern UI development' },
        { name: 'TypeScript', icon: <SiTypescript className="text-4xl text-blue-600" />, description: 'Type-safe JavaScript' },
        { name: 'Next.js', icon: <SiReact className="text-4xl text-gray-900" />, description: 'Server-side rendering' },
        { name: 'React Native', icon: <SiReact className="text-4xl text-blue-400" />, description: 'Cross-platform mobile' }
      ]
    },
    {
      title: 'Cloud Platforms',
      icon: <FaCloud className="text-3xl" />,
      color: 'from-orange-500 to-red-600',
      technologies: [
        { name: 'Amazon AWS', icon: <SiAmazon className="text-4xl text-orange-500" />, description: 'Comprehensive cloud services' },
        { name: 'Google Cloud', icon: <SiGooglecloud className="text-4xl text-blue-600" />, description: 'ML & data platforms' },
        { name: 'Microsoft Azure', icon: <FaMicrosoft className="text-4xl text-blue-600" />, description: 'Enterprise cloud solutions' },
        { name: 'Docker', icon: <SiDocker className="text-4xl text-blue-500" />, description: 'Container technology' }
      ]
    },
    {
      title: 'Databases & Storage',
      icon: <FaDatabase className="text-3xl" />,
      color: 'from-purple-500 to-pink-600',
      technologies: [
        { name: 'PostgreSQL', icon: <SiPostgresql className="text-4xl text-blue-700" />, description: 'Relational database' },
        { name: 'MongoDB', icon: <SiMongodb className="text-4xl text-green-600" />, description: 'Document database' },
        { name: 'Redis', icon: <SiRedis className="text-4xl text-red-600" />, description: 'In-memory data store' },
        { name: 'MySQL', icon: <FaDatabase className="text-4xl text-blue-600" />, description: 'Popular SQL database' }
      ]
    },
    {
      title: 'DevOps & Infrastructure',
      icon: <FaCogs className="text-3xl" />,
      color: 'from-cyan-500 to-blue-600',
      technologies: [
        { name: 'Kubernetes', icon: <SiKubernetes className="text-4xl text-blue-600" />, description: 'Container orchestration' },
        { name: 'Terraform', icon: <SiTerraform className="text-4xl text-purple-600" />, description: 'Infrastructure as Code' },
        { name: 'GitHub Actions', icon: <SiGithubactions className="text-4xl text-gray-900" />, description: 'CI/CD automation' },
        { name: 'Jenkins', icon: <SiJenkins className="text-4xl text-red-600" />, description: 'Continuous integration' }
      ]
    },
    {
      title: 'Messaging & Streaming',
      icon: <FaMobile className="text-3xl" />,
      color: 'from-yellow-500 to-orange-600',
      technologies: [
        { name: 'Apache Kafka', icon: <SiApachekafka className="text-4xl text-gray-900" />, description: 'Event streaming platform' },
        { name: 'RabbitMQ', icon: <SiRabbitmq className="text-4xl text-orange-600" />, description: 'Message broker' },
        { name: 'Redis Pub/Sub', icon: <SiRedis className="text-4xl text-red-600" />, description: 'Real-time messaging' },
        { name: 'AWS SQS/SNS', icon: <SiAmazon className="text-4xl text-orange-500" />, description: 'Cloud messaging' }
      ]
    }
  ];

  return (
    <section id="technology" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Technology Stack
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We master modern technologies and proven frameworks to build production-ready solutions.
            Our engineers stay current with industry best practices while maintaining deep expertise in battle-tested tools.
          </p>
        </div>

        {/* Technology Categories */}
        <div className="space-y-12">
          {categories.map((category, index) => (
            <div key={index}>
              {/* Category Header */}
              <div className="flex items-center space-x-4 mb-6">
                <div className={`bg-gradient-to-r ${category.color} p-4 rounded-lg text-white`}>
                  {category.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900">{category.title}</h3>
              </div>

              {/* Technologies Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {category.technologies.map((tech, idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border border-gray-200"
                  >
                    <div className="flex flex-col items-center text-center">
                      <div className="mb-4">
                        {tech.icon}
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2">{tech.name}</h4>
                      <p className="text-gray-600 text-sm">{tech.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Additional Skills */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-12 text-white">
          <h3 className="text-3xl font-bold mb-6 text-center">Beyond the Stack</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-2xl font-bold mb-2">Microservices</div>
              <p className="text-blue-100 text-sm">Architecture & Design Patterns</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold mb-2">Security</div>
              <p className="text-blue-100 text-sm">OAuth2, JWT, OWASP Best Practices</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold mb-2">Testing</div>
              <p className="text-blue-100 text-sm">Unit, Integration, E2E Testing</p>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold mb-2">Monitoring</div>
              <p className="text-blue-100 text-sm">ELK, Prometheus, Grafana</p>
            </div>
          </div>
        </div>

        {/* Technology Selection Philosophy */}
        <div className="mt-16 bg-white rounded-2xl shadow-lg p-12 border border-gray-200">
          <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">Our Technology Philosophy</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🎯</span>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Right Tool for the Job</h4>
              <p className="text-gray-600 text-sm">
                We choose technologies based on your specific requirements, not trends or personal preference.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🛡️</span>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Battle-Tested Solutions</h4>
              <p className="text-gray-600 text-sm">
                We prioritize proven, stable technologies with strong community support and long-term viability.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">📈</span>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Future-Proof Architecture</h4>
              <p className="text-gray-600 text-sm">
                We design systems that can evolve with changing requirements and technology landscapes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechStack;
